// /////////////////////////////////////////////////////////////
// [ファイル] SfaInlineFields.tsx
// [概要] 営業案件詳細のインライン編集用の部品（推進案件機能統合）。共通の InlineEdit の上に作る。
//        InlineCustomerField … CRM 顧客 1 人を選ぶ（地主・仲介業者）
//        InlineUnitCustomersField … 区画の顧客一覧（複数・代表者・持分）をその場で直す
//        useLostReasons … フェーズを失注(7)にするときの失注理由の選択肢
//        どれも詳細画面の編集モードのときだけ鉛筆と下線が出る（保存は ✓ を押したときだけ）。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useState } from 'react';
import { Box, Chip, IconButton, Radio, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import * as Icons from '../../constants/icons';
import { InlineEdit } from '../InlineEditField';
import { useNotification } from '../../providers/NotificationProvider';
import CustomerPicker, { CustomerOption } from './CustomerPicker';
import { PURCHASE_LOST_REASON, SfaUnitCustomer, sfaStructureApi, customerLabel, fmtShare, parseShare } from '../../hooks/useSfaStructure';

// 失注理由の選択肢（仕入の同時失注に使う purchase_lost は人が選ばない）。フェーズの入力欄を開いたときに読む。
// all を付けると purchase_lost も含める（理由の名前を表示するとき）
export function useLostReasons(all = false): { reason_code: string; reason_name: string }[] {
  const [reasons, setReasons] = useState<{ reason_code: string; reason_name: string }[]>([]);
  useEffect(() => {
    let alive = true;
    sfaStructureApi.masters()
      .then((m) => { if (alive) setReasons(all ? m.lost_reasons : m.lost_reasons.filter((r) => r.reason_code !== PURCHASE_LOST_REASON)); })
      .catch(() => { if (alive) setReasons([]); });
    return () => { alive = false; };
  }, [all]);
  return reasons;
}

// --------------------------------------------------
// CRM 顧客 1 人。required なら空にできない（地主）。corporateOnly は法人だけ（仲介業者）
// --------------------------------------------------
export function InlineCustomerField({ value, name, label, onSave, required, corporateOnly, emptyText = '-' }: {
  value: string | null | undefined;
  name: string | null | undefined;
  label: string;
  onSave: (customerId: string | null) => Promise<void>;
  required?: boolean;
  corporateOnly?: boolean;
  emptyText?: string;
}) {
  const current: CustomerOption | null = value ? { id: value, display_name: name || '', customer_type: corporateOnly ? 'corporate' : 'individual' } : null;
  return (
    <InlineEdit<CustomerOption | null>
      value={current} label={label} editMinWidth={320}
      display={<Typography component="span" variant="body2">{value ? (name || '') : emptyText}</Typography>}
      isEqual={(a, b) => (a?.id || null) === (b?.id || null)}
      validate={(v) => (required && !v ? `${label}を選んでください` : null)}
      onSave={(v) => onSave(v?.id || null)}
      renderEditor={({ draft, setDraft, saving }) => (
        <CustomerPicker label={label} value={draft} onChange={setDraft} corporateOnly={corporateOnly} required={required} disabled={saving} />
      )}
    />
  );
}

// --------------------------------------------------
// 区画の顧客（買主・施主など。複数可・代表者 1 人・持分は任意）。保存すると一覧を丸ごと置き換える
// --------------------------------------------------
interface CustomerRow { option: CustomerOption; share: string; is_representative: boolean }

const rowKey = (r: CustomerRow) => `${r.option.id}:${r.share.trim()}:${r.is_representative ? 1 : 0}`;

export function InlineUnitCustomersField({ customers, onSave }: {
  customers: SfaUnitCustomer[];
  onSave: (next: SfaUnitCustomer[]) => Promise<void>;
}) {
  const current: CustomerRow[] = customers.map((c) => ({
    option: { id: c.customer_id, display_name: c.display_name || '', customer_type: c.customer_type === 'corporate' ? 'corporate' : 'individual' },
    share: fmtShare(c.share_numerator, c.share_denominator),
    is_representative: !!c.is_representative,
  }));
  return (
    <InlineEdit<CustomerRow[]>
      value={current} label="顧客" editMinWidth={440}
      display={<Typography component="span" variant="body2">{customers.map(customerLabel).join('、') || '-'}</Typography>}
      isEqual={(a, b) => a.map(rowKey).join('|') === b.map(rowKey).join('|')}
      onSave={async (rows) => {
        // 持分の書式は保存前に確かめる（読めないときは入力欄に理由を出し、入力を残す）
        const out: SfaUnitCustomer[] = [];
        for (const c of rows) {
          const share = parseShare(c.share);
          if (share === undefined) throw new Error(`${c.option.display_name} の持分は「1/2」のように入力してください`);
          if (share && (share.num <= 0 || share.den <= 0 || share.num > share.den)) throw new Error(`${c.option.display_name} の持分は 1 以上で分母以下の分数にしてください`);
          out.push({ customer_id: c.option.id, share_numerator: share?.num ?? null, share_denominator: share?.den ?? null, is_representative: c.is_representative });
        }
        await onSave(out);
      }}
      renderEditor={({ draft, setDraft, saving }) => <CustomersEditor rows={draft} onChange={setDraft} disabled={saving} />}
    />
  );
}

function CustomersEditor({ rows, onChange, disabled }: { rows: CustomerRow[]; onChange: (v: CustomerRow[]) => void; disabled: boolean }) {
  const notify = useNotification();
  const [picker, setPicker] = useState<CustomerOption | null>(null);
  const add = (o: CustomerOption | null) => {
    if (!o) return;
    if (rows.some((r) => r.option.id === o.id)) { notify.warning('同じ顧客が既にあります'); return; }
    onChange([...rows, { option: o, share: '', is_representative: rows.length === 0 }]);
    setPicker(null);
  };
  // 代表者を消したら先頭を代表者にする
  const remove = (i: number) => {
    const rest = rows.filter((_, j) => j !== i);
    onChange(rest.some((r) => r.is_representative) ? rest : rest.map((r, j) => ({ ...r, is_representative: j === 0 })));
  };
  return (
    <Box>
      {rows.length > 0 && (
        <Table size="small">
          <TableHead><TableRow><TableCell>代表</TableCell><TableCell>顧客</TableCell><TableCell>持分（任意）</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.option.id}>
                <TableCell><Radio size="small" checked={r.is_representative} disabled={disabled} onChange={() => onChange(rows.map((x, j) => ({ ...x, is_representative: j === i })))} /></TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" color={r.option.customer_type === 'individual' ? 'success' : 'info'} label={r.option.customer_type === 'individual' ? '個人' : '法人'} sx={{ mr: 1 }} />
                  {r.option.display_name}
                </TableCell>
                <TableCell><TextField size="small" value={r.share} placeholder="例 1/2" disabled={disabled} onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, share: e.target.value } : x)))} sx={{ width: 110 }} /></TableCell>
                <TableCell><IconButton size="small" disabled={disabled} onClick={() => remove(i)} aria-label="この顧客を外す"><Icons.Close fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Box sx={{ mt: 1 }}><CustomerPicker label="顧客を追加" value={picker} onChange={add} disabled={disabled} /></Box>
    </Box>
  );
}
